import { createProject, StudioProject } from '@/lib/knots-api'
import { StudioRoute, useGetProjects, useUpdateRoute } from '@/lib/react-query'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { RouteType, Runtime } from '@/config/app'
import { useButtonNavigateClick } from '@/hooks/useButtonNavigateClick'

/**
 * Hook for handling creating missing project for logged-in user. Returns main project and other existing projects
 * created in the past for existing customers
 * @returns Tuple of `[mainProject, legacyProjects]`
 */
export const useProject = (): [StudioProject, StudioProject[]] => {
  const { data: projects } = useGetProjects()

  const { data: mainProject } = useSuspenseQuery({
    queryKey: ['mainProject'],
    queryFn: async () => {
      if (projects?.length) return projects[0]
      return await createProject(`Main project ${Math.floor(Math.random() * 10000)}`, 'Main project for all routes')
    },
    staleTime: Infinity,
  })

  const legacyProjects = useMemo(
    () => projects?.filter((project) => project.id !== mainProject.id) ?? [],
    [projects, mainProject]
  )

  return [mainProject, legacyProjects]
}

export const useOpenRoute = (routeId: number, projectId: number) => {
  return useButtonNavigateClick(`/projects/${projectId}/${routeId}`)
}

export const useSetActive = (route: StudioRoute, projectId: number) => {
  const updateRoute = useUpdateRoute(projectId, route.id!)

  return useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      // Cancel event on opening route via click on table row
      event.stopPropagation()

      await updateRoute.mutateAsync({
        route: {
          active: !route.active,
        },
      })
    },
    [route.id, route.active]
  )
}
export const getRouteRuntimeLabel = (runtime: Runtime) => {
  const labels = {
    [Runtime.NodeJS]: 'Node.js',
    [Runtime.NodeJS18]: 'Node.js 18',
    [Runtime.NodeJS22]: 'Node.js 22',
    [Runtime.Python3]: 'Python 3',
    [Runtime.Python3_10]: 'Python 3.10',
  }

  return labels[runtime] ?? runtime ?? '-'
}
export const getRouteTypeLabel = (type: RouteType) => {
  const labels = {
    [RouteType.HTTP]: 'HTTP',
    [RouteType.HTTP_V2]: 'HTTP V2',
    [RouteType.Mail]: 'E-Mail',
    [RouteType.Cron]: 'Cron',
    [RouteType.Flow]: 'Flow'
  }

  return labels[type] ?? type ?? '-'
}
